//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// login-welcome imports
import { loginWelcomeFragments } from "./LoginWelcome.gql";
import { useStyles, agreement, link, signupBtn } from "./LoginWelcome.styles";
// other internals
import { LoginBtn } from "@components/login-btn";
import { H1, P, A } from "@ui";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";

// util
import { PostMessageManager } from "@util/post-message.util";
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
// constants
import { paths } from "@constants/site.constant";
// state
import { IReduxState } from "@reducers/root.reducer";
import { hasSeenLoginPage } from "@state/local-storage";
import { loginUser } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { setOnboardingMeta } from "@actions/onboarding.actions";

// material-ui
import { LinearProgress } from "@material-ui/core";
import { OnboardingType, OnboardingTypeSource } from "@components/onboarding-nux/OnboardingNUX.d";
import { CSSProperties } from "@material-ui/core/styles/withStyles";

// schema
import * as ISchema from "@schema";

import { RelayProp } from "react-relay";

interface IContainer {
  viewer: ISchema.IViewer;
  redirect?: string;
  ambiguous?: boolean;
  isNewUser?: boolean;
}

interface IProps extends IContainer {
  relay: RelayProp;
  snackBarOpen: (message: String) => void;
  isChromeExtension: boolean;
  setOnboardingMeta: typeof setOnboardingMeta;
  style?: CSSProperties;
}

//////////////////
// COMPONENT
//////////////////

const LoginWelcomeCmpt = (props: IProps) => {
  const classes = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [hideCookieErrorDialog, setHideCookieErrorDialog] = React.useState(true);

  React.useEffect(() => {
    const { redirect, isNewUser, isChromeExtension } = props;
    page(PageEvents.WELCOME, {
      redirect: redirect,
      isNewUser: !!isNewUser ? isNewUser : false,
    });
    hasSeenLoginPage.set(true);
    if (isChromeExtension) {
      PostMessageManager.openSidebar(true);
    }
  }, []);

  React.useEffect(() => {
    setLoading(false);
  }, [props.viewer.user]);

  const enableWelcomeLoginOnboardingType = () => {
    props.setOnboardingMeta(OnboardingType.Default, OnboardingTypeSource.WelcomeLoginPage);
  };

  const onLoginStart = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    setLoading(true);
  };

  const onLoginSuccess = () => {
    enableWelcomeLoginOnboardingType();
  };

  const onLoginFailure = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    setLoading(false);
  };

  const renderCookieDialog = () => {
    return (
      <CookieDialog isChromeExtension={props.isChromeExtension} open={!hideCookieErrorDialog} />
    );
  };

  const onGoogleFailure = (e: any) => {
    if (e.error === "idpiframe_initialization_failed") {
      setHideCookieErrorDialog(false);
    }
  };

  return (
    <React.Fragment>
      {renderCookieDialog()}
      <div className={classes.container}>
        <div className={classes.paper}>
          <H1 className={classes.header}>👋 Welcome to Clockwise!</H1>
          <div className={classes.subhead}>Let’s make your calendar work for you.</div>
          <div className={classes.buttonContainer}>
            <LoginBtn
              label={"Connect your Google Calendar"}
              googleStandard
              loginUserMutation={loginUser}
              style={signupBtn.base}
              labelStyle={signupBtn.label}
              redirect={props.redirect}
              onStart={onLoginStart}
              onSuccess={onLoginSuccess}
              onFailure={onLoginFailure}
              onGoogleFailure={onGoogleFailure}
            />
          </div>
          <P style={agreement}>
            By signing in, you agree to Clockwise's{" "}
            <A style={link} target="_blank" href={paths.terms}>
              {" "}
              Terms of Service
            </A>{" "}
            and{" "}
            <A style={link} target="_blank" href={paths.privacy}>
              Privacy Policy
            </A>
            .
          </P>

          {loading ? <LinearProgress className={classes.loadingBar} variant="indeterminate" /> : ""}
        </div>
      </div>
    </React.Fragment>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
  setOnboardingMeta,
})(LoginWelcomeCmpt);

//////////////////
// RELAY
//////////////////
export const LoginWelcome = createFragmentContainer<IContainer>(
  reduxContainer,
  loginWelcomeFragments,
);
